import { useRef, useState, type FormEvent } from 'react';
import { Secao } from './Secao.tsx';
import { Icone } from './Icone.tsx';
import { maiuscula } from '../lib/caixa.ts';
import type {
  Anotacao,
  Contato,
  Contrato,
  FormaPagamento,
  SituacaoContrato,
} from '../lib/api.ts';
import { apiContratos } from '../lib/api.ts';
import { SITUACOES_DO_CONTRATO as SITUACOES } from '../lib/rotulos.ts';
import { paraTela } from '../lib/datas.ts';
import { usarEhMaster } from '../lib/papel.ts';
import { Valor } from './Valor.tsx';
import { BlocoDeLancar, CampoDoPasso } from './PassoAPasso.tsx';
import '../estilos/formularios-curtos-2026.css';
import '../estilos/linha-rapida-2026.css';

const FORMAS: Record<FormaPagamento, string> = {
  a_vista: 'à vista',
  entrada_parcelas: 'entrada + parcelas',
  parcelado: 'parcelado',
};


/* ── Contratos ─────────────────────────────────────────────────── */

export function SecaoContratos({
  contratos,
  hoje,
  aoNovo,
  aoEditar,
  aoCorrigirCobrancas,
  aoAceitar,
  aoBaixar,
  aoMudarSituacao,
  aoApagar,
  filtro,
  aoLimparFiltro,
  abrirEm,
}: {
  contratos: Contrato[];
  hoje: string;
  /**
   * Quais situações mostrar. `null` é o padrão da casa: o que está vivo —
   * proposta e ativo. Vem dos números da ficha, que são botões.
   */
  filtro: SituacaoContrato[] | null;
  aoLimparFiltro: () => void;
  abrirEm: number;
  aoNovo: () => void;
  aoEditar: (c: Contrato) => void;
  aoCorrigirCobrancas: (c: Contrato) => void;
  aoAceitar: (c: Contrato) => void;
  aoBaixar: (contratoId: string, parcelaId: string, pagar: boolean) => void;
  aoMudarSituacao: (contratoId: string, situacao: SituacaoContrato) => void;
  aoApagar: (contratoId: string) => void;
}) {
  const [confirmando, setConfirmando] = useState<string | null>(null);
  // O contrato é do master de ponta a ponta: criar, editar, mudar situação e
  // dar baixa são todos atos sobre dinheiro, e o colaborador não vê dinheiro.
  // O servidor recusa as quatro rotas; aqui a tela deixa de oferecer.
  const ehMaster = usarEhMaster();

  /*
   * ⚠️ **Por padrão a lista mostra só o que está vivo** — proposta e ativo.
   * Pedido do Richard, 15/08/2026.
   *
   * Concluído, recusado e cancelado são história: consulta-se de vez em quando,
   * não se olha todo dia. Deixá-los sempre na lista faria a proposta que espera
   * resposta — a única linha que pede ação hoje — disputar espaço com contrato
   * de dois anos atrás.
   *
   * **Mas nada fica fora de alcance.** Os cinco números da ficha filtram esta
   * lista, e é por eles que se chega ao concluído e ao recusado — inclusive
   * para devolver um contrato a Ativo, que é o caminho que o Richard pediu em
   * 15/08/2026. Esconder é uma coisa; tirar do alcance seria outra.
   */
  const VIVOS: SituacaoContrato[] = ['proposta', 'ativo'];
  const mostrados = contratos.filter((c) => (filtro ?? VIVOS).includes(c.situacao));

  const cartao = (c: Contrato) => {
          const atrasadas = c.parcelas.filter((p) => !p.pagoEm && p.vencimentoEm < hoje);
          return (
            <article
              key={c.id}
              className={
                /*
                  ⚠️ **A cor do cartão vem do nível da PESQUISA quando há uma só.**
                  Com mais de um nível o cartão fica **NEUTRO**: pintar na cor de
                  um deles é escolher um e apagar o outro.

                  ⛔ **Lia os CASOS até 29/08/2026**, e por isso um contrato que
                  vendeu N3 e N1 saía pintado inteiro de vinho — a cor do único
                  caso vinculado. Agora sai neutro, que é a verdade.
                */
                `contrato nivel-${
                  c.niveisDoContrato.length === 1
                    ? c.niveisDoContrato[0]!.nivel
                    : c.niveisDoContrato.length > 1
                      ? 'misto'
                      : (c.nivel ?? 'customizado')
                }` +
                // Proposta não é "fechada": está viva, esperando resposta.
                (c.situacao === 'recusada' || c.situacao === 'cancelado' || c.situacao === 'concluido'
                  ? ' contrato-fechado'
                  : '')
              }
            >
              <div className="contrato-topo">
                <div className="contrato-identidade">
                  <span className="dado contrato-codigo">{c.codigo}</span>
                  {/*
                    ⛔ **AS TARJAS DE NÍVEL SAÍRAM DAQUI — decisão dele, 30/08/2026.**

                    > *"Prefiro manter apenas a tag do nível na frente do nome do
                    > investigado. Para não ficar repetindo as tags, retire as
                    > tags dos níveis de cima."* — Richard

                    Com duas pesquisas, o mesmo nível aparecia três vezes no
                    cartão: aqui em cima, ao lado do investigado, e na cor da
                    borda. **Repetição não é ênfase — é ruído.**

                    ⚠️ **A tarja não sumiu do cartão: ela DESCEU.** Agora anda
                    junto da linha que diz o que foi vendido, logo abaixo — e é
                    lá que ela informa, porque diz QUAL pesquisa investiga QUEM.

                    ⚠️ **`c.niveisDoContrato` continua vindo do servidor** e é o
                    que decide a COR do cartão, alguns pontos acima. Não o
                    remova achando que ficou órfão.
                  */}
                  <span className={`etiqueta etiqueta-${c.situacao}`}>{SITUACOES[c.situacao]}</span>
                </div>
                {ehMaster && (
                  <select
                    className="caso-situacao s-seletor"
                    value={c.situacao}
                    onChange={(e) => aoMudarSituacao(c.id, e.target.value as SituacaoContrato)}
                    aria-label={`Situação do contrato ${c.codigo}`}
                  >
                    {(Object.keys(SITUACOES) as SituacaoContrato[]).map((s) => (
                      <option key={s} value={s}>
                        {SITUACOES[s]}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/*
                ⚠️ **OS INVESTIGADOS SAEM DE CADA PESQUISA, e não do `objeto`.**

                > *"Gostaria que todos os investigados principais que forem
                > colocados no contrato aparecessem nesse card."* — Richard,
                > 29/08/2026

                ⛔ **Aqui estava `c.objeto`** — uma linha só, escrita na criação
                do contrato. Ele acrescentou uma segunda pesquisa com outro
                investigado e o card continuou mostrando o primeiro: `objeto` é
                anterior ao modelo de 28/08, em que **cada pesquisa passou a ter
                os SEUS investigados**.

                ⭐ O próprio schema já dizia por quê: *"um campo no contrato
                inteiro obrigaria duas pesquisas de investigados diferentes a
                compartilhar a mesma lista — e o cliente leria que a Insight
                cobre quem ela não cobre"*.

                ⚠️ **A tarja aparece SEMPRE, mesmo com uma pesquisa só — e isso
                DIVERGE do PDF de propósito.** Em 30/08 o Richard tirou as tarjas
                do cabeçalho: aqui passou a ser **o único lugar do cartão** onde
                o nível se lê. Condicioná-la a "mais de uma pesquisa" faria o
                nível sumir de todo contrato de pesquisa única.

                ⚠️ **No PDF a regra continua sendo `itens.length > 1`, e está
                certo lá:** o documento tem uma seção própria dizendo o que foi
                contratado, então o nível já aparece em outro lugar. **São
                contextos diferentes, e a divergência é deliberada** — quem
                mexer num não deve "consertar" o outro. O outro é
                `lib/documento-proposta.ts`.

                ⚠️ **`entendimento` é TEXTO com um investigado por linha** — é
                assim que ele abre a proposta. Cada linha vira uma linha aqui.
              */}
              {(() => {
                const comInvestigado = c.itens.filter((i) => i.entendimento?.trim());
                if (comInvestigado.length === 0) {
                  /*
                   * Contrato anterior a 28/08, ou pesquisa sem investigado
                   * preenchido: `objeto` é a única verdade que existe.
                   *
                   * ⚠️ **E as tarjas vêm JUNTO.** Sem elas o nível sumiria do
                   * cartão inteiro, agora que ele não está mais no cabeçalho.
                   *
                   * ⚠️ **Desde 17/09/2026 o `objeto` de contrato NOVO é o
                   * próprio nome do nível** — e escrevê-lo ao lado da tarja
                   * faria o cartão dizer "N1 · INSIGHT   N1 · INSIGHT". Só sai
                   * quando acrescenta alguma coisa: o apelido escrito à mão dos
                   * contratos antigos, que é o único caso em que ele informa.
                   */
                  /* ⚠️ Sem caixa: o servidor grava o `objeto` em maiúsculas
                     (é dado identificável), e "KAIRÓS Customizado" volta como
                     "KAIRÓS CUSTOMIZADO" — comparar cru deixaria passar. */
                  const niveisEmTexto = c.niveisDoContrato
                    .map((n) => n.nome)
                    .join(' + ')
                    .toLocaleUpperCase('pt-BR');
                  const objetoAcrescenta =
                    c.objeto.trim() !== '' && c.objeto.trim().toLocaleUpperCase('pt-BR') !== niveisEmTexto;
                  return (
                    <p className="contrato-investigados">
                      {c.niveisDoContrato.map((n) => (
                        <span key={n.nivel} className={`selo-nivel selo-${n.nivel}`}>{n.nome}</span>
                      ))}
                      {objetoAcrescenta && <span>{c.objeto}</span>}
                    </p>
                  );
                }
                return (
                  <div className="contrato-objeto">
                    {comInvestigado.map((i) => (
                      <p key={i.id} className="contrato-investigados">
                        <span className={`selo-nivel selo-${i.nivel}`}>{i.nivelNome}</span>
                        <span>
                          {i.entendimento!.split('\n').filter((l) => l.trim()).map((linha, n) => (
                            <span key={n} className="linha-de-investigado">{linha.trim()}</span>
                          ))}
                        </span>
                      </p>
                    ))}
                  </div>
                );
              })()}

              {/*
                ⛔⛔ **NA CORTESIA O QUADRO DE NÚMEROS NÃO TEM NÚMEROS —
                11/09/2026.**

                Ele mostrava "Valor R$ 0,00 · Recebido R$ 0,00 · A receber
                R$ 0,00 · Pagamento: a definir no aceite". Quatro células
                afirmando um dinheiro que não existe, e uma prometendo um
                aceite que a rota recusa. ⚠️ **Zero repetido não lê como
                ausência: lê como cobrança não paga.**
              */}
              {c.cortesia ? (
                <div className="caso-numeros">
                  <div>
                    <span className="rotulo">Contrato</span>
                    <span className="valor-forte">Cortesia</span>
                  </div>
                  <div>
                    {/* ⭐ 29/09/2026: "Cobrança: não há" lia-se como "nada a pagar",
                        e o Fundo de Custas é depositado. O que não cobra é o honorário. */}
                    <span className="rotulo">Honorários</span>
                    <span>sem cobrança</span>
                  </div>
                  <div>
                    <span className="rotulo">Fundo de Custas</span>
                    {/* ⭐ 29/09/2026: a cortesia de honorários tem fundo; só a total não. */}
                    {c.cortesiaTotal ? <span>não há</span> : <Valor de={c.fundoDeCustas} />}
                  </div>
                </div>
              ) : (
              <div className="caso-numeros">
                <div>
                  <span className="rotulo">Valor</span>
                  <Valor de={c.valorTotal} className="valor-forte" />
                </div>
                <div>
                  <span className="rotulo">Recebido</span>
                  <Valor de={c.recebido} />
                </div>
                <div>
                  <span className="rotulo">A receber</span>
                  <Valor de={c.aReceber} />
                </div>
                {c.fundoDeCustas && (
                  <div>
                    <span className="rotulo">Fundo de Custas</span>
                    <Valor de={c.fundoDeCustas} />
                  </div>
                )}
                <div>
                  <span className="rotulo">Pagamento</span>
                  <span>{c.ehProposta ? 'a definir no aceite' : FORMAS[c.formaPagamento]}</span>
                </div>
              </div>
              )}

              {/* A barra é desenho de dinheiro. Sem os valores ela não teria o
                  que desenhar: `Number(null) / Number(null)` dá `NaN`, e uma
                  barra vazia ao lado de "R$ ••••" pareceria dívida quitada. */}
              {/* ⛔ **Cortesia enchia a barra em 100% — 11/09/2026.**
                  `Number(0) / Number(0)` é `NaN`, mas `Math.min(100, NaN)`
                  devolve `NaN` e o CSS ignora a largura inválida, deixando a
                  barra cheia: um contrato sem cobrança nenhuma parecia quitado.
                  Sem denominador não há progresso a desenhar. */}
              {!c.valoresOcultos && !c.ehProposta && Number(c.valorTotal) > 0 && (
                <div
                  className="progresso"
                  role="img"
                  aria-label={`Recebido ${c.recebido} de ${c.valorTotal}`}
                >
                  <span
                    style={{
                      width: `${Math.min(100, (Number(c.recebido) / Number(c.valorTotal)) * 100)}%`,
                    }}
                  />
                </div>
              )}

              {atrasadas.length > 0 && (
                <ul className="caso-avisos aviso-vencido">
                  <li>
                    {atrasadas.length === 1
                      ? '1 cobrança vencida e não baixada.'
                      : `${atrasadas.length} cobranças vencidas e não baixadas.`}
                  </li>
                </ul>
              )}

              {/*
                ⭐⭐ **CORTESIA: o aviso de aceite não se aplica — 10/09/2026.**

                ⛔ "Proposta ainda não aceita. As cobranças nascem no aceite"
                num contrato de cortesia cobra uma providência que não existe:
                não há cobrança para nascer. A frase ficaria pendurada na ficha
                para sempre, e pendência falsa some com a força das verdadeiras.

                ⚠️ **A proposta continua podendo ser gerada** — ele pode querer
                mostrar ao parceiro o que foi feito. O que muda é que o Hub não
                a cobra nem a espera.
              */}
              {c.cortesia ? (
                <p className="ajuda ajuda-solta">
                  {c.cortesiaTotal ? (
                    <>
                      <strong>Cortesia total.</strong> Sem valor e sem fundo de custas: as
                      despesas são da KAIRÓS. O caso segue com prazo, investigados e relatório
                      do nível contratado.
                    </>
                  ) : (
                    <>
                      <strong>Cortesia de honorários.</strong> Os honorários não geram cobrança;
                      o Fundo de Custas segue o rito normal, com depósito, prestação de contas e
                      devolução do saldo.
                      {c.ehProposta ? ' Proposta ainda não aceita: no aceite entra a data da assinatura.' : ''}
                    </>
                  )}
                </p>
              ) : (
                c.ehProposta && (
                  <p className="ajuda ajuda-solta">
                    Proposta ainda não aceita. As cobranças nascem no aceite —
                    {c.validadeDias ? ` válida por ${c.validadeDias} dias.` : ''}
                  </p>
                )
              )}

              {!c.ehProposta && (
              <table className="parcelas">
                <tbody>
                  {c.parcelas.map((p) => {
                    const vencida = !p.pagoEm && p.vencimentoEm < hoje;
                    return (
                      <tr key={p.id} className={p.pagoEm ? 'parcela-paga' : ''}>
                        <td className="parcela-rotulo">
                          {p.numero === 0 ? 'Entrada' : `Parcela ${p.numero}`}
                        </td>
                        <td className="parcela-valor">
                          <Valor de={p.valor} />
                        </td>
                        <td className={'dado parcela-data' + (vencida ? ' vencido' : '')}>
                          {paraTela(p.vencimentoEm)}
                          {/* Só acontece com data escrita à mão: o que o Hub
                              gera já sai adiado para o próximo dia útil. */}
                          {p.emDiaNaoUtil && (
                            <span className="parcela-nao-util" title={`Cai em ${p.feriado ?? 'fim de semana'}`}>
                              {' '}·{' '}{p.feriado ?? 'fim de semana'}
                            </span>
                          )}
                        </td>
                        <td className="parcela-acao">
                          {!ehMaster ? (
                            // Declarar que entrou dinheiro é do master. O
                            // colaborador continua vendo o que já foi pago —
                            // isso é situação do cliente, não valor.
                            p.pagoEm ? (
                              <span className="ajuda">pago {paraTela(p.pagoEm)}</span>
                            ) : (
                              <span className="ajuda">{vencida ? 'vencida' : 'em aberto'}</span>
                            )
                          ) : p.pagoEm ? (
                            <button
                              type="button"
                              className="botao-texto"
                              onClick={() => aoBaixar(c.id, p.id, false)}
                              title={`Pago em ${paraTela(p.pagoEm)}`}
                            >
                              pago {paraTela(p.pagoEm)}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="botao-texto"
                              onClick={() => aoBaixar(c.id, p.id, true)}
                            >
                              Dar baixa
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}

              <div className="caso-acoes s-acoes">
                {confirmando === c.id ? (
                  <>
                    <span className="item-confirma">Apagar o contrato e as cobranças?</span>
                    <button
                      type="button"
                      className="botao-texto botao-perigo"
                      onClick={() => {
                        aoApagar(c.id);
                        setConfirmando(null);
                      }}
                    >
                      Sim
                    </button>
                    <button
                      type="button"
                      className="botao-texto"
                      onClick={() => setConfirmando(null)}
                    >
                      Não
                    </button>
                  </>
                ) : (
                  <>
                    {c.casos > 0 && (
                      <span className="ajuda">
                        cobre {c.casos} caso{c.casos === 1 ? '' : 's'}
                      </span>
                    )}
                    {/* Contrato concluído ou cancelado não se edita nem se
                        apaga: é registro fechado. Para reabrir, basta voltar a
                        situação para Ativo no seletor acima — o caminho existe,
                        e passa por uma decisão consciente. */}
                    {/* O PDF sai de qualquer contrato que liste o que foi
                        vendido — inclusive depois de aceito, para reimprimir o
                        que foi oferecido.
                        ⛔ **Conserto de 14/09/2026:** a condição lia `c.nivel`,
                        o campo antigo que "nada lê" desde 28/08 — e contrato
                        lançado já com as pesquisas (itens) tem esse campo vazio.
                        O botão sumia justo no contrato de cortesia. Quem diz o
                        que foi vendido são os itens, e a rota do PDF já
                        conferia os itens. */}
                    {ehMaster && c.itens.length > 0 && (
                      <a
                        className="botao-texto"
                        href={apiContratos.proposta(c.id)}
                        target="_blank"
                        rel="noopener"
                      >
                        Proposta em PDF
                      </a>
                    )}
                    {/* ⭐ 29/09/2026: a cortesia se aceita, como a paga — o aceite
                        grava a assinatura e não fabrica parcela nenhuma. */}
                    {ehMaster && c.ehProposta && (
                      <button
                        type="button"
                        className="botao-texto botao-aceitar"
                        onClick={() => aoAceitar(c)}
                      >
                        Aceitar
                      </button>
                    )}
                    {!ehMaster ? null : c.situacao === 'proposta' ? (
                      <button type="button" className="botao-texto" onClick={() => aoEditar(c)}>
                        Editar
                      </button>
                    ) : c.situacao === 'ativo' ? (
                      <>
                        <button type="button" className="botao-texto" onClick={() => aoEditar(c)}>
                          Editar
                        </button>
                        {/* ⛔ Cortesia não tem cobrança a corrigir: a tela
                            abria vazia e não deixava salvar nada. */}
                        {!c.cortesia && (
                          <button
                            type="button"
                            className="botao-texto"
                            onClick={() => aoCorrigirCobrancas(c)}
                          >
                            Corrigir cobranças
                          </button>
                        )}
                        <button
                          type="button"
                          className="botao-texto botao-perigo"
                          onClick={() => setConfirmando(c.id)}
                        >
                          Apagar
                        </button>
                      </>
                    ) : (
                      <span className="ajuda">
                        registro fechado · volte para Ativo para editar
                      </span>
                    )}
                  </>
                )}
              </div>
            </article>
          );
  };

  return (
    <Secao
      id="contratos"
      desenho="folha"
      titulo="Contratos"
      subtitulo="A proposta e o contrato são a mesma ficha: o aceite muda a situação."
      contador={mostrados.length}
      abrirEm={abrirEm}
      acao={
        ehMaster ? (
          <button type="button" className="botao-primario s-botao s-botao-p" onClick={aoNovo}>
            <Icone nome="mais" tamanho={14} />
            Novo contrato
          </button>
        ) : null
      }
    >
      {filtro && (
        <p className="filtro-ativo">
          Mostrando {filtro.map((f) => SITUACOES[f].toLowerCase()).join(' e ')}.{' '}
          <button type="button" className="botao-texto" onClick={aoLimparFiltro}>
            Ver os em andamento
          </button>
        </p>
      )}

      {mostrados.length === 0 ? (
        <p className="vazio">
          {filtro
            ? 'Nenhum contrato nesta situação.'
            : contratos.length === 0
              ? 'Nenhum contrato ainda.'
              : 'Nada em andamento. Use os números acima para ver os encerrados.'}
        </p>
      ) : (
        mostrados.map(cartao)
      )}
    </Secao>
  );
}

/* ── Contatos ──────────────────────────────────────────────────── */

export function SecaoContatos({
  contatos,
  aoCriar,
  aoEditar,
  aoApagar,
}: {
  contatos: Contato[];
  aoCriar: (dados: DadosContato) => Promise<void>;
  aoEditar: (id: string, dados: DadosContato) => Promise<void>;
  aoApagar: (id: string) => void;
}) {
  const [abrindo, setAbrindo] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  // Cadastrar contato é do colaborador também; corrigir e apagar, não.
  const ehMaster = usarEhMaster();

  return (
    <Secao
      id="contatos"
      desenho="folha"
      titulo="Contatos"
      subtitulo="Com quem se fala no cliente."
      contador={contatos.length}
      acao={
        <button
          type="button"
          className="botao-secundario s-botao s-botao-p"
          onClick={() => {
            setEditando(null);
            setAbrindo(!abrindo);
          }}
        >
          {!abrindo && <Icone nome="mais" tamanho={14} />}
          {abrindo ? 'Cancelar' : 'Novo contato'}
        </button>
      }
    >
      {abrindo && (
        <FormularioContato
          rotuloAcao="Adicionar"
          aoSalvar={async (dados) => {
            await aoCriar(dados);
            setAbrindo(false);
          }}
          aoCancelar={() => setAbrindo(false)}
        />
      )}

      {contatos.length === 0 ? (
        <p className="vazio">Nenhum contato cadastrado.</p>
      ) : (
        <ul className="lista-contatos">
          {contatos.map((c) =>
            editando === c.id ? (
              <li key={c.id} className="contato-editando">
                <FormularioContato
                  inicial={c}
                  rotuloAcao="Salvar"
                  aoSalvar={async (dados) => {
                    await aoEditar(c.id, dados);
                    setEditando(null);
                  }}
                  aoCancelar={() => setEditando(null)}
                />
              </li>
            ) : (
              <li key={c.id}>
                {/* ⭐ As iniciais do contato, no desenho de 14/09/2026. */}
                <span className="s-avatar" aria-hidden="true">
                  {c.nome.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p.charAt(0)).join('')}
                </span>
                <div className="contato-corpo">
                  <p className="contato-nome">
                    {c.nome}
                    {c.principal && <span className="etiqueta etiqueta-principal">principal</span>}
                  </p>
                  {c.cargo && <p className="contato-cargo">{c.cargo}</p>}
                  {(c.email || c.telefone) ? (
                    <p className="contato-canais">
                      {c.email && (
                        <span>
                          <Icone nome="email" tamanho={13} />
                          {c.email}
                        </span>
                      )}
                      {c.telefone && (
                        <span>
                          <Icone nome="telefone" tamanho={13} />
                          <span className="dado">{c.telefone}</span>
                        </span>
                      )}
                    </p>
                  ) : (
                    !c.cargo && <p className="contato-meta">—</p>
                  )}
                </div>
                {ehMaster && (
                  <div className="contato-acoes">
                    {/* ⭐ Ícones com nome falado (14/09/2026): na coluna estreita,
                        as duas palavras espremiam o nome do contato. */}
                    <button
                      type="button"
                      className="s-acao s-acao-icone"
                      onClick={() => {
                        setAbrindo(false);
                        setEditando(c.id);
                      }}
                      title="Editar contato"
                      aria-label={`Editar ${c.nome}`}
                    >
                      <Icone nome="lapis" tamanho={14} />
                    </button>
                    <button
                      type="button"
                      className="s-acao s-acao-icone perigo"
                      onClick={() => aoApagar(c.id)}
                      title="Apagar contato"
                      aria-label={`Apagar ${c.nome}`}
                    >
                      <Icone nome="lixeira" tamanho={14} />
                    </button>
                  </div>
                )}
              </li>
            ),
          )}
        </ul>
      )}
    </Secao>
  );
}

export type DadosContato = {
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  principal: boolean;
};

/**
 * O formulário do contato, um só para cadastrar e para corrigir.
 *
 * Dois formulários iguais lado a lado divergem: alguém acrescenta um campo num
 * e esquece do outro, e a correção passa a não alcançar o que o cadastro
 * grava. Aqui o `inicial` é a única diferença entre os dois usos.
 */
function FormularioContato({
  inicial,
  rotuloAcao,
  aoSalvar,
  aoCancelar,
}: {
  inicial?: Contato;
  rotuloAcao: string;
  aoSalvar: (dados: DadosContato) => Promise<void>;
  aoCancelar: () => void;
}) {
  const [nome, setNome] = useState(inicial?.nome ?? '');
  const [cargo, setCargo] = useState(inicial?.cargo ?? '');
  const [email, setEmail] = useState(inicial?.email ?? '');
  const [telefone, setTelefone] = useState(inicial?.telefone ?? '');
  const [principal, setPrincipal] = useState(inicial?.principal ?? false);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  // Os `id` levam o do contato junto: dois formulários abertos na mesma página
  // com o mesmo `id` fariam o rótulo apontar para o campo errado, e quem usa
  // leitor de tela ouviria o nome de outro campo.
  const marca = inicial?.id ?? 'novo';

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!nome.trim() || emVoo.current) return;
    emVoo.current = true;
    setSalvando(true);
    try {
      await aoSalvar({
        nome: nome.trim(),
        cargo: cargo.trim() || null,
        email: email.trim() || null,
        telefone: telefone.trim() || null,
        principal,
      });
    } finally {
      emVoo.current = false;
      setSalvando(false);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (o bloco de lançar): cada campo diz o
     que é, e "principal" virou chave. O lugar e o que o botão faz, os mesmos. */
  return (
    <BlocoDeLancar
      titulo={inicial ? `Editar ${inicial.nome}` : 'Novo contato'}
      aoEnviar={(e) => void enviar(e)}
      acoes={
        <>
          <button type="button" className="imv-bt fantasma" onClick={aoCancelar}>
            Cancelar
          </button>
          <button type="submit" className="imv-bt primario" disabled={!nome.trim() || salvando}>
            <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : rotuloAcao}
          </button>
        </>
      }
    >
      <CampoDoPasso rotulo="Nome" obrigatorio col={3}>
        <input id={`cn-${marca}`} value={nome} onChange={(e) => setNome(maiuscula(e.target.value))} autoFocus />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Cargo" col={3}>
        <input id={`cc-${marca}`} value={cargo} onChange={(e) => setCargo(maiuscula(e.target.value))} />
      </CampoDoPasso>
      <CampoDoPasso rotulo="E-mail" col={3}>
        <input id={`ce-${marca}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Telefone" col={3} mono>
        <input id={`ct-${marca}`} value={telefone} onChange={(e) => setTelefone(e.target.value)} />
      </CampoDoPasso>
      <div className="imv-campo c6 fc-principal">
        <button type="button" role="switch" aria-checked={principal} className={`imv-op neutro${principal ? ' on' : ''}`} onClick={() => setPrincipal(!principal)}>
          <i />
          Contato principal
        </button>
      </div>
    </BlocoDeLancar>
  );
}

/* ── Anotações ─────────────────────────────────────────────────── */

export function SecaoAnotacoes({
  anotacoes,
  aoCriar,
  aoEditar,
  aoApagar,
}: {
  anotacoes: Anotacao[];
  aoCriar: (texto: string) => Promise<void>;
  aoEditar: (id: string, texto: string) => Promise<void>;
  aoApagar: (id: string) => void;
}) {
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState('');
  // Escrever no diário é de todo mundo. Reescrever o que o outro escreveu, não
  // — anotação é registro do relacionamento, e registro corrigido em silêncio
  // deixa de servir de registro.
  const ehMaster = usarEhMaster();

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!texto.trim() || salvando) return;
    setSalvando(true);
    try {
      await aoCriar(texto.trim());
      setTexto('');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Secao
      id="anotacoes"
      desenho="folha"
      titulo="Anotações"
      subtitulo="O diário do relacionamento."
      contador={anotacoes.length}
    >
      <form onSubmit={enviar} className="form-embutido">
        <div className="campo">
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="O que aconteceu, o que foi combinado…"
            aria-label="Nova anotação"
          />
        </div>
        {/* ⭐ Linha rápida, 18/09/2026: os botões do desenho novo, do mesmo
            tamanho aqui e na correção logo abaixo. */}
        <div className="dialogo-acoes">
          <button type="submit" className="imv-bt primario p" disabled={!texto.trim() || salvando}>
            <Icone nome="check" tamanho={13} /> {salvando ? 'Salvando…' : 'Anotar'}
          </button>
        </div>
      </form>

      {anotacoes.length === 0 ? (
        <p className="vazio">Nenhuma anotação ainda.</p>
      ) : (
        <ol className="linha-do-tempo">
          {anotacoes.map((a) => (
            <li key={a.id}>
              <div className="anotacao-cabeca">
                <span className="dado anotacao-quando">
                  {new Date(a.criadoEm).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'America/Sao_Paulo',
                  })}
                </span>
                <span className="anotacao-autor">{a.autor}</span>
                {ehMaster && editando !== a.id && (
                  <button
                    type="button"
                    className="s-acao s-acao-icone"
                    onClick={() => {
                      setEditando(a.id);
                      setRascunho(a.texto);
                    }}
                    title="Editar anotação"
                    aria-label="Editar anotação"
                  >
                    <Icone nome="lapis" tamanho={14} />
                  </button>
                )}
                {ehMaster && (
                  <button
                    type="button"
                    className="s-acao s-acao-icone perigo"
                    onClick={() => aoApagar(a.id)}
                    title="Apagar anotação"
                    aria-label="Apagar anotação"
                  >
                    <Icone nome="lixeira" tamanho={14} />
                  </button>
                )}
              </div>

              {editando === a.id ? (
                <div className="anotacao-edicao">
                  <span className="imv-in lr-texto">
                    <textarea
                      value={rascunho}
                      onChange={(e) => setRascunho(e.target.value)}
                      aria-label="Editar anotação"
                      autoFocus
                    />
                  </span>
                  <div className="dialogo-acoes">
                    <button
                      type="button"
                      className="imv-bt fantasma p"
                      onClick={() => setEditando(null)}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="imv-bt primario p"
                      disabled={!rascunho.trim()}
                      onClick={async () => {
                        await aoEditar(a.id, rascunho.trim());
                        setEditando(null);
                      }}
                    >
                      <Icone nome="check" tamanho={13} /> Salvar
                    </button>
                  </div>
                </div>
              ) : (
                <p className="anotacao-texto">{a.texto}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Secao>
  );
}
